import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { api, setDevUser, setImpersonate, ApiError } from '../lib/api.js';
import type { Theme, TimeFormat } from '@ehr/shared';
import { setPersonalTimeFormat } from '../lib/timePrefs.js';
import { setPersonalTheme } from '../lib/theme.js';
import { clearChartLinks } from '../lib/viewAsSwitch.js';
import { followSwitch } from '../lib/sessionGate.js';

export interface SessionUser {
  id: string;
  email: string;
  /** ehr#264 - "First Last" from the users row; null when the profile has no name yet. */
  name?: string | null;
  role: string;
  orgId: string;
  /** True when an admin is viewing the app as this (other) user. */
  impersonating?: boolean;
  /** The real admin's email when impersonating. */
  actorEmail?: string;
  /** ehr#264 - the real admin's name when impersonating. */
  actorName?: string | null;
  /** ehr#98 - this person's own time format, and the practice's client-facing one. */
  personalTimeFormat?: TimeFormat;
  clientTimeFormat?: TimeFormat;
  /** ehr#420 - this person's own theme: light, dark, or the device's ('system'). */
  personalTheme?: Theme;
}

/**
 * ehr#264 - the name to show for a signed-in person: their first + last name, else the
 * email's local part (a login email is not a name: Derek Brooks signs in as patel@...).
 */
export const displayName = (
  name: string | null | undefined,
  email: string | null | undefined,
  fallback = '',
) => name?.trim() || (email ? (email.split('@')[0] ?? email) : fallback);

interface SessionValue {
  user: SessionUser | null;
  loading: boolean;
  /** HTTP status of the last /auth/me failure (401 = no session, 403 = not provisioned). */
  authStatus: number | null;
  /** Dev impersonation (local only) — sets the x-dev-user header and re-fetches. */
  impersonate: (email: string) => void;
  devEmail: string | null;
  /** Admin "view as" (prod) — sets x-impersonate-email and re-fetches. */
  viewAs: (email: string) => void;
  /** Stop impersonating and return to the real admin identity. */
  stopViewingAs: () => void;
  /** True while an admin is impersonating another user. */
  impersonating: boolean;
  /** ehr#98 - apply a newly saved personal time format (re-renders the app). */
  setTimeFormat: (f: TimeFormat) => void;
  /** ehr#420 - apply a newly saved personal theme at once. */
  setTheme: (t: Theme) => void;
}

const Ctx = createContext<SessionValue | null>(null);

// Seeded dev users for the local role switcher (mirrors the prototype's picker).
export const DEV_USERS = [
  { email: 'admin@demo.credifyfast.com', label: 'Marcus Rivera — Admin' },
  { email: 'manager@demo.credifyfast.com', label: 'Sofia Nguyen — Manager' },
  { email: 'patel@demo.credifyfast.com', label: 'Derek Brooks — Clinician' },
  { email: 'okafor@demo.credifyfast.com', label: 'Aisha Khan — Clinician' },
  { email: 'chen@demo.credifyfast.com', label: 'Liam Connor — Clinician' },
  { email: 'priya@demo.credifyfast.com', label: 'Priya Patel — Clinician' },
  { email: 'vasquez@demo.credifyfast.com', label: 'Jennifer Park — Supervisor' },
  { email: 'frontdesk@demo.credifyfast.com', label: 'Nina Alvarez — Front Desk' },
  { email: 'haddad@demo.credifyfast.com', label: 'Omar Haddad — Front Desk' },
];

export function SessionProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [authStatus, setAuthStatus] = useState<number | null>(null);
  const [devEmail, setDevEmail] = useState<string | null>(() => localStorage.getItem('devUser'));
  const [impEmail, setImpEmail] = useState<string | null>(() =>
    localStorage.getItem('impersonate'),
  );

  useEffect(() => {
    setDevUser(devEmail);
    setImpersonate(impEmail);
    setLoading(true);
    let cancelled = false;
    let attempt = 0;
    const fetchMe = () => {
      api
        .get<SessionUser>('/auth/me')
        .then((u) => {
          if (cancelled) return;
          setPersonalTimeFormat(u.personalTimeFormat); // ehr#98
          setPersonalTheme(u.personalTheme); // ehr#420
          setUser(u);
          setAuthStatus(null);
          sessionStorage.removeItem('ehr.sso.redirected'); // reset the one-shot login redirect
          setLoading(false);
        })
        .catch((e: unknown) => {
          if (cancelled) return;
          const status = e instanceof ApiError ? e.status : 0;
          // A rate-limit (429) or transient server error is NOT a lost session —
          // the SSO cookie is still valid. Retry a few times (staying in the
          // loading state) rather than bouncing to the login screen.
          if ((status === 429 || status >= 500) && attempt < 3) {
            attempt += 1;
            setTimeout(fetchMe, 1500 * attempt);
            return;
          }
          // 401 = no SSO session, 403 = valid session but not provisioned/ambiguous.
          setAuthStatus(e instanceof ApiError ? e.status : null);
          if (!(e instanceof ApiError)) console.error(e);
          setUser(null);
          setLoading(false);
        });
    };
    fetchMe();
    return () => {
      cancelled = true;
    };
  }, [devEmail, impEmail]);

  // ehr#659: View as (and the local dev user) live in localStorage, shared by every EHR tab, but each
  // tab read them once at start: after a switch in one tab the others kept showing the person
  // before (their tasks, their counts) until reloaded. A switch made in another tab is followed here.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      const next = followSwitch(e.key, e.newValue);
      if (!next) return;
      clearChartLinks();
      if (next.key === 'impersonate') setImpEmail(next.value);
      else setDevEmail(next.value);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // ehr#622 (reopened 10-24): switching who the EHR shows drops the chart links of the person
  // shown before (App also mounts the page body fresh for the new person).
  const impersonate = (email: string) => {
    clearChartLinks();
    localStorage.setItem('devUser', email);
    setDevEmail(email);
  };

  const viewAs = (email: string) => {
    clearChartLinks();
    localStorage.setItem('impersonate', email);
    setImpEmail(email);
  };

  const stopViewingAs = () => {
    clearChartLinks();
    localStorage.removeItem('impersonate');
    setImpEmail(null);
  };

  // Trust the server: impersonation is only real if requireUser honored it.
  const impersonating = Boolean(user?.impersonating);
  const setTimeFormat = (f: TimeFormat) => {
    setPersonalTimeFormat(f);
    setUser((u) => (u ? { ...u, personalTimeFormat: f } : u));
  };
  const setTheme = (t: Theme) => {
    setPersonalTheme(t);
    setUser((u) => (u ? { ...u, personalTheme: t } : u));
  };

  return (
    <Ctx.Provider
      value={{
        user,
        loading,
        authStatus,
        impersonate,
        devEmail,
        viewAs,
        stopViewingAs,
        impersonating,
        setTimeFormat,
        setTheme,
      }}
    >
      {children}
    </Ctx.Provider>
  );
}

export function useSession(): SessionValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('useSession must be used within SessionProvider');
  return v;
}

export function canManage(role: string | undefined): boolean {
  return role === 'admin' || role === 'manager';
}
